import React, { useEffect, useState } from "react";
import { apiClient } from "../../integrationQA/utils/apiClient";

const states = [
  { key: "loading", label: "Loading" },
  { key: "success", label: "Success" },
  { key: "empty", label: "Empty" },
  { key: "error", label: "Error" }
];

export default function MetricDetails({ metric, onBack, range = "7" }) {
  const [apiState, setApiState] = useState("loading");
  const [manualState, setManualState] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [apiError, setApiError] = useState("");
  const [detailData, setDetailData] = useState(null);
  const state = manualState || apiState;

  useEffect(() => {
    let active = true;
    setApiState("loading");
    setApiError("");
    apiClient(`/api/analytics/summary?range=${range}`)
      .then((response) => {
        if (!active) return;
        setDetailData(response?.data || null);
        setApiState(response?.data ? "success" : "empty");
      })
      .catch((error) => {
        if (!active) return;
        setApiError(error?.message || "The analytics API could not return this metric.");
        setApiState("error");
      });
    return () => { active = false; };
  }, [metric.id, refreshKey, range]);

  const fieldMap = {
    "engagement-overview": ["dateRange", "totalMessages", "totalSessions", "uniqueVisitors"],
    "widget-opens": ["dateRange", "widgetOpens", "uniqueVisitors", "openRate"],
    "conversations-started": ["dateRange", "conversationsStarted", "uniqueVisitors", "startRate"],
    "messages-session": ["dateRange", "messages", "sessions", "messagesPerSession"],
    "returning-visitors": ["dateRange", "returningVisitors", "totalVisitors", "returnRate"],
    "ai-resolution-rate": ["dateRange", "resolvedByAI", "totalConversations", "resolutionRate"],
    "escalation-rate": ["dateRange", "escalated", "totalConversations", "escalationRate"],
    "response-time": ["dateRange", "avgResponseTime", "p50", "p95"],
    "unresolved-questions": ["dateRange", "unresolvedQuestions", "totalQuestions", "unresolvedRate"],
    "cta": ["dateRange", "impressions", "clicks", "clickThroughRate"],
    "lead-conversion": ["dateRange", "capturedLeads", "convertedLeads", "conversionRate"]
  };

  const fields = fieldMap[metric.id] || [];
  const current = detailData || {};
  const currentValue = metric.id === "engagement-overview" ? Number(current.engagement?.totalMessages || 0).toLocaleString()
    : metric.id === "widget-opens" ? Number(current.widgetOpens || 0).toLocaleString()
    : metric.id === "conversations-started" ? Number(current.conversationsStarted || 0).toLocaleString()
    : metric.id === "messages-session" ? Number(current.messagesPerSession || 0).toFixed(1)
    : metric.id === "returning-visitors" ? `${current.returningVisitorsRate || 0}%`
    : metric.id === "ai-resolution-rate" ? `${current.aiResolutionRate || 0}%`
    : metric.id === "escalation-rate" ? `${current.escalationRate || 0}%`
    : metric.id === "response-time" ? `${current.averageResponseTime || 0}s`
    : metric.id === "unresolved-questions" ? Number(current.unresolvedQuestions || 0).toLocaleString()
    : metric.id === "cta" ? `${Number(current.cta?.impressions || 0).toLocaleString()} / ${Number(current.cta?.clicks || 0).toLocaleString()}`
    : `${Number(current.leadConversion?.capturedLeads || 0).toLocaleString()} / ${current.leadConversion?.conversionRate || 0}%`;

  return (
    <div className="metric-detail-page">
      <div className="detail-topbar">
        <button className="back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left"></i> Back to Analytics
        </button>
        <span className="detail-badge">Analytics &amp; Reports</span>
      </div>

      <div className="detail-header">
        <div className={`metric-icon large ${metric.colorClass}`}>
          <i className={`bi ${metric.icon}`}></i>
        </div>
        <div>
          <h1>{metric.label}</h1>
          <p>{metric.description}</p>
        </div>
      </div>

      <div className="state-tabs">
        {states.map((item) => (
          <button
            key={item.key}
            className={state === item.key ? "active" : ""}
            onClick={() => setManualState(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {state === "loading" && (
        <div className="state-panel">
          <div className="spinner-border text-primary"></div>
          <h3>Loading {metric.label}</h3>
          <p>Fetching data from the analytics API...</p>
        </div>
      )}

      {state === "empty" && (
        <div className="state-panel">
          <i className="bi bi-inbox"></i>
          <h3>No data available</h3>
          <p>Try another date range or check whether the metric has received events.</p>
        </div>
      )}

      {state === "error" && (
        <div className="state-panel error-panel">
          <i className="bi bi-exclamation-triangle"></i>
          <h3>Something went wrong</h3>
          <p>{apiError || "The analytics API could not return this metric."}</p>
          <button className="btn btn-primary" onClick={() => { setManualState(null); setRefreshKey((value) => value + 1); }}>Retry</button>
        </div>
      )}

      {state === "success" && (
        <div className="detail-content">
          <div className="detail-kpi-card">
            <span>Current value · Last {range} days</span>
            <strong>{currentValue}</strong>
            <small>{metric.helper}</small>
          </div>

          <div className="field-card">
            <h3>Frontend field map</h3>
            <div className="field-list">
              {fields.map((field) => (
                <div className="field-item" key={field}>
                  <code>{field}</code>
                  <span>API response field</span>
                </div>
              ))}
            </div>
          </div>

          <div className="field-card">
            <h3>Validation &amp; API states</h3>
            <div className="check-row"><i className="bi bi-check-circle-fill"></i> Date range is required</div>
            <div className="check-row"><i className="bi bi-check-circle-fill"></i> Numeric values are formatted before display</div>
            <div className="check-row"><i className="bi bi-check-circle-fill"></i> Loading state is shown while fetching</div>
            <div className="check-row"><i className="bi bi-check-circle-fill"></i> Empty state is shown when data is unavailable</div>
            <div className="check-row"><i className="bi bi-check-circle-fill"></i> Error state includes retry action</div>
          </div>
        </div>
      )}
    </div>
  );
}